import { DateTime } from 'luxon';
import { CookieManager } from '../index.ts';
import FX_CURRECIES from '../constants/fx_currencies.js';

type Day = {
    date: number;
    day: string;
};

type Week = Day[];

type Month = Week[];

const currencies = ['BTC', 'ETH', 'USDT', 'USDC', ...FX_CURRECIES];

const forexMarketHours = [
    { name: 'Sydney', open: '21:00', close: '6:00' },
    { name: 'Tokyo', open: '00:00', close: '9:00' },
    { name: 'London', open: '7:00', close: '16:00' },
    { name: 'New-York', open: '12:00', close: '21:00' }
];

export function toFix(value: string | number, fixed = 2) {
    if (isNaN(+value)) return 0;

    const num = parseFloat(String(value));
    if (Number.isInteger(num)) return num;

    const stringValue = num.toString();
    const decimalIndex = stringValue.indexOf('.');
    if (decimalIndex === -1) return num;

    const fractionalPart = stringValue.slice(decimalIndex + 1);

    const firstSignificantIndex = fractionalPart.search(/[^0]/);
    if (firstSignificantIndex === -1) return num;

    const fix = firstSignificantIndex + fixed;

    const multiplier = 10 ** fix;
    return Math.round(num * multiplier) / multiplier;
}

export function addSpaces(num: string | number) {
    if (num) {
        const str = num.toString();
        const regex = /\B(?=(\d{3})+(?!\d))/g;
        if (regex.test(str)) {
            if (str.includes('.')) {
                let strArr = str.split('.');
                strArr[0] = strArr[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                return strArr.join('.');
            } else {
                return str.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            }
        } else {
            return str;
        }
    } else {
        return num;
    }
}
export function calculatePNLFX(
    position: any,
    marketData: { bid: number; ask: number }
) {
    const entryPrice = parseFloat(position.entry_price);

    const positionAmount = position.amount;
    const pair = position.pair.toLowerCase();
    const side = position.side;
    const leverage = position.leverage;

    const getMultiplier = (pair: string): number => {
        if (
            pair.endsWith('sek') ||
            pair.endsWith('mxn') ||
            pair.endsWith('jpy')
        )
            return 1000;
        if (pair.startsWith('xau')) return 100;
        if (
            pair.endsWith('f.t') ||
            pair.endsWith('.b') ||
            pair.endsWith('xpd') ||
            pair.endsWith('xag') ||
            pair.endsWith('xbr') ||
            pair.endsWith('xng') ||
            pair.endsWith('xpt') ||
            pair.endsWith('xti')
        )
            return 100;
        if (pair === 'cocoa' || pair === 'coffee') return 1;
        if (pair === 'brent') return 100;
        else return 100000;
    };

    const multiplier = getMultiplier(pair);

    let pnl = 0;
    let pnl_roi = 0;

    if (side === 'LONG') {
        pnl = (marketData.bid - entryPrice) * positionAmount * multiplier;
        pnl_roi = pnl / ((positionAmount * entryPrice * multiplier) / leverage);
    } else if (side === 'SHORT') {
        pnl = (entryPrice - marketData.ask) * positionAmount * multiplier;
        pnl_roi = pnl / ((positionAmount * entryPrice * multiplier) / leverage);
    }

    return { pnl, pnl_roi };
}

export function calculatePNL(position: any, marketPrice: any) {
    const entryPrice = parseFloat(position.entry_price);
    const positionAmount = position.amount;
    const multiplier = position.multiplier;
    const side = position.side;

    let pnl = 0;
    let pnlToBalanceRatio = 0;

    if (side === 'SHORT') {
        pnl = (entryPrice - marketPrice) * Math.abs(positionAmount);
        pnlToBalanceRatio =
            (((entryPrice - marketPrice) * positionAmount * multiplier) /
                (entryPrice * Math.abs(positionAmount))) *
            100;
    } else if (side === 'LONG') {
        pnl = (marketPrice - entryPrice) * Math.abs(positionAmount);
        pnlToBalanceRatio =
            (((marketPrice - entryPrice) * positionAmount * multiplier) /
                (entryPrice * Math.abs(positionAmount))) *
            100;
    }

    return { pnl, pnlToBalanceRatio };
}

export function toDynamicFix(value: string | number): number {
    if (isNaN(+value)) return 0;

    const num = parseFloat(String(value));
    if (Number.isInteger(num)) return num;

    const stringValue = num.toString();
    const decimalIndex = stringValue.indexOf('.');
    if (decimalIndex === -1) return num;

    const fractionalPart = stringValue.slice(decimalIndex + 1);

    const firstSignificantIndex = fractionalPart.search(/[^0]/);
    if (firstSignificantIndex === -1) return num;

    const fix = firstSignificantIndex + 4;

    const multiplier = 10 ** fix;
    return Math.round(num * multiplier) / multiplier;
}

export function numFormat(value: string | number) {
    let parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}

export function maskData(
    isMask: boolean,
    value: any,
    options: {
        repeat?: number;
        mask?: string;
        prefix?: string;
        suffix?: string;
    } = {}
): any {
    const { repeat = 4, mask = '*', prefix = '', suffix = '' } = options;

    let finalPrefix, finalSuffix;

    if (isMask) {
        // change currency
        finalPrefix = prefix === '$' ? getCurrencySymbol() : prefix;
        finalSuffix = suffix === '$' ? getCurrencySymbol() : suffix;

        return finalPrefix + mask.repeat(repeat) + finalSuffix;
    } else {
        return value;
    }
}

// return key=1&key=2 || only active
export function transformKeysToQuery(keys: any) {
    return keys.reduce((accumulator: any, value: any, index: number) => {
        if (index === 0) {
            return `key=${encodeURIComponent(value.id)}`;
        } else {
            return accumulator + `&key=${encodeURIComponent(value.id)}`;
        }
    }, '');
}

export function getTimezoneOffset() {
    function isFloat(n: number) {
        return Number(n) === n && n % 1 !== 0;
    }
    function toUTCOffset(n: number) {
        let nString = String(n);
        if (nString.includes('.75')) {
            return nString.replace('.75', ':45');
        } else if (nString.includes('.25')) {
            return nString.replace('.25', ':15');
        } else {
            return nString.replace('.5', ':30');
        }
    }
    const timezone_offset =
        Number(CookieManager.getItem('scope360::timezone')) || 0;

    const timezone_number = isFloat(timezone_offset)
        ? toUTCOffset(timezone_offset)
        : timezone_offset;

    return `UTC${timezone_offset >= 0 ? '+' : ''}${timezone_number}`;
}

export function getFullYear(value: any) {
    return new Date(value).getFullYear();
}

export function toBaseDate(value: any, offset = true) {
    const offsetString = offset ? getTimezoneOffset() : 'UTC+0';
    const dt = DateTime.fromISO(value, { setZone: true }).setZone(offsetString);
    return dt.toFormat('dd LLLL, y, TT');
}

export function formatDate(value: any, format = 'dd/LL/y', offset = true) {
    const offsetString = offset ? getTimezoneOffset() : 'UTC+0';
    const dt = DateTime.fromISO(value, { setZone: true }).setZone(offsetString);
    return dt.toFormat(format);
}

export function getFormattedDate(date: Date) {
    return date.toISOString().split('T')[0];
}

export function getMonth(value: any) {
    const monthsArray = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];
    return monthsArray[new Date(value).getMonth()];
}

export function getDate(value: any) {
    return new Date(value).getDate();
}

export function getDay(value: any) {
    const dt = DateTime.fromISO(value, { setZone: true });
    return dt.toFormat('cccc');
}

export function getDaysDifference(dateString: string) {
    // Parse the input date
    const inputDate: any = new Date(dateString);

    // Get the current date
    const currentDate: any = new Date();

    // Calculate the difference in time
    const differenceInTime = currentDate - inputDate;

    // Convert the difference in time to days
    const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

    return differenceInDays;
}

export function getCurrencySymbol(): string {
    const currencySymbols: { [key: string]: string } = {
        USD: '$',
        EUR: '€',
        JPY: '¥',
        GBP: '£',
        AUD: '$',
        CAD: '$',
        CHF: 'Fr.',
        CNY: '¥',
        INR: '₹',
        BRL: 'R$',
        ZAR: 'R',
        MXN: '$',
        NOK: 'kr',
        SGD: '$',
        NZD: '$',
        HKD: 'hk$',
        SEK: 'kr',
        DKK: 'kr',
        HRK: 'kn',
        EGP: '£'
    };

    const currency: string | undefined = getCookieCurrency();

    if (currency && currencySymbols[currency?.split('::')[0]]) {
        return currencySymbols[currency?.split('::')[0]];
    }

    return currencySymbols['USD'];
}

export function isFx(): boolean {
    if (window !== undefined) {
        return (
            window.location.hostname.includes('fx') ||
            window.location.host === 'localhost:5173'
        );
    } else {
        return false;
    }
}

export function getCookieCurrency() {
    return isFx()
        ? CookieManager.getItem('scope360::currency_fx')
        : CookieManager.getItem('scope360::currency');
}

export function getCurrencyRate(): number {
    const currency: string | undefined = getCookieCurrency();

    if (currency && currency?.split('::')[1]) {
        return +currency?.split('::')[1];
    }

    return 1;
}

export const withSign = (
    value: any,
    options: {
        isPercentage?: boolean;
        isFixed?: boolean;
        isPlus?: boolean;
        isDynamicFix?: boolean;
    } = {
        isPercentage: false,
        isFixed: true,
        isPlus: false,
        isDynamicFix: false
    }
) => {
    let sign;
    if (value >= 0) {
        sign = options.isPlus && value !== 0 ? '+' : '';
    } else {
        sign = '-';
    }
    let newValue = Math.abs(value);

    if (options.isDynamicFix) {
        newValue = toDynamicFix(newValue);
    } else if (options.isFixed) {
        newValue = toFix(newValue);
    }
    if (isFx()) {
        return options.isPercentage
            ? addSpaces(`${sign}${newValue}%`)
            : addSpaces(`${sign}${getCurrencySymbol()}${toFix(newValue)}`);
    }
    return options.isPercentage
        ? addSpaces(`${sign}${newValue}%`)
        : addSpaces(
              `${sign}${getCurrencySymbol()}${toFix(
                  newValue * getCurrencyRate()
              )}`
          );
};

export function calculateTimeDifference(
    startTime: any,
    endTime: any,
    isTable = false
): string {
    const difference: any = +new Date(endTime) - +new Date(startTime);
    return getFormattedDuration(difference, isTable);
}

function parseTimezoneOffset(offsetString: string): number {
    const matches = offsetString.match(/([+-]?)(\d+):?(\d+)?/);
    if (!matches) return 0; // fallback if the format is unexpected

    const sign = matches[1] === '-' ? -1 : 1;
    const hours = parseInt(matches[2], 10);
    const minutes = matches[3] ? parseInt(matches[3], 10) : 0;

    return sign * (hours + minutes / 60); // Convert minutes to fractional hours
}

export function convertMarketHoursToUserTime(
    timezoneOffset = getTimezoneOffset()
) {
    function parseTime(timeString: string): number {
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours + minutes / 60; // Convert time to hours as a float
    }

    function formatTime(time: number): string {
        const hours = Math.floor(time);
        const minutes = (time % 1) * 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
            2,
            '0'
        )}`;
    }

    const userOffset = parseTimezoneOffset(timezoneOffset);

    return forexMarketHours.map((market) => {
        const openUserTime = (parseTime(market.open) + userOffset + 24) % 24;
        const closeUserTime = (parseTime(market.close) + userOffset + 24) % 24;

        return {
            name: market.name,
            open: formatTime(openUserTime),
            close: formatTime(closeUserTime)
        };
    });
}
export function getMarketStatus(timezone: string = getTimezoneOffset()) {
    const currentTime = new Date();
    const userOffset = parseTimezoneOffset(timezone);

    const currentTimeInSeconds =
        ((currentTime.getUTCHours() + userOffset + 24) % 24) * 3600 +
        currentTime.getUTCMinutes() * 60 +
        currentTime.getUTCSeconds();

    function timeToSeconds(time: string): number {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60;
    }

    const activeMarkets = forexMarketHours.filter(({ open, close }) => {
        const openTime =
            (timeToSeconds(open) + userOffset * 3600 + 86400) % 86400;
        const closeTime =
            (timeToSeconds(close) + userOffset * 3600 + 86400) % 86400;

        return openTime < closeTime
            ? currentTimeInSeconds >= openTime &&
                  currentTimeInSeconds < closeTime
            : currentTimeInSeconds >= openTime ||
                  currentTimeInSeconds < closeTime;
    });

    if (activeMarkets.length > 1) {
        const overlapEndInSeconds = Math.min(
            ...activeMarkets.map(
                ({ close }) =>
                    (timeToSeconds(close) + userOffset * 3600 + 86400) % 86400
            )
        );
        return {
            activeMarkets: activeMarkets.map(({ name }) => name),
            overlapTime: Math.max(
                0,
                Math.floor((overlapEndInSeconds - currentTimeInSeconds) / 60)
            )
        };
    }

    if (activeMarkets.length === 1) {
        return {
            activeMarkets: activeMarkets.map(({ name }) => name),
            overlapTime: 0
        };
    }

    const nextMarket = forexMarketHours
        .map(({ name, open }) => ({
            name,
            timeUntilOpenInSeconds:
                (timeToSeconds(open) + userOffset * 3600 + 86400) % 86400 >=
                currentTimeInSeconds
                    ? ((timeToSeconds(open) + userOffset * 3600 + 86400) %
                          86400) -
                      currentTimeInSeconds
                    : 86400 -
                      currentTimeInSeconds +
                      ((timeToSeconds(open) + userOffset * 3600 + 86400) %
                          86400)
        }))
        .reduce((a, b) =>
            a.timeUntilOpenInSeconds < b.timeUntilOpenInSeconds ? a : b
        );

    return {
        activeMarkets: [],
        nextMarket: nextMarket.name,
        timeUntilNextOpen: Math.floor(nextMarket.timeUntilOpenInSeconds / 60),
        timeUntilNextOpenInSeconds: nextMarket.timeUntilOpenInSeconds
    };
}

export function yTicksCallback(data: any, isMask: boolean) {
    if (data >= 1000 || data <= -1000)
        return maskData(isMask, toFix(data / 1000, 2) + 'k', { repeat: 3 });
    else return maskData(isMask, toFix(data, 2), { repeat: 3 });
}

export function getFormattedDuration(duration: any, isTable = false) {
    const days = Math.floor(duration / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
        (duration % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);

    if (isTable) {
        if (days > 0) {
            return `${days} day${days > 1 ? 's' : ''} ${hours} hr${
                hours > 1 ? 's' : ''
            }`;
        }
        if (hours > 0) {
            return `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min${
                minutes > 1 ? 's' : ''
            }`;
        }
        if (minutes > 0) {
            return `${minutes} min${minutes > 1 ? 's' : ''} ${seconds} sec${
                seconds > 1 ? 's' : ''
            }`;
        }
        return `${seconds} sec`;
    } else {
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.join(' ');
    }
}

export function addSlashToPair(pair: string) {
    const suffixMatch = pair.match(/(-[A-Za-z0-9]+)$/);
    const suffix = suffixMatch ? suffixMatch[1] : '';
    const pairWithoutSuffix = suffix ? pair.slice(0, -suffix.length) : pair;

    const regex = new RegExp(`^(.*?)(${currencies.join('|')})$`);
    const matches = pairWithoutSuffix.match(regex);

    if (matches) {
        const firstPart = matches[1];
        const secondPart = matches[2];
        if (firstPart && secondPart) {
            return `${firstPart}/${secondPart}${suffix}`;
        }
    }

    for (let i = 1; i < pairWithoutSuffix.length; i++) {
        const firstPart = pairWithoutSuffix.substring(0, i);
        const secondPart = pairWithoutSuffix.substring(i);

        if (currencies.includes(firstPart) && currencies.includes(secondPart)) {
            return `${firstPart}/${secondPart}${suffix}`;
        }
    }

    return pair;
}

export function addSlashToFxPair(pair: string) {
    const currencies = [
        'BTC',
        'ETH',
        'USDT',
        'USD',
        'EUR',
        'GBP',
        'JPY',
        'CAD',
        'CHF',
        'USDC',
        'CNH'
    ];

    for (let i = 1; i < pair.length; i++) {
        const firstPart = pair.substring(0, i);
        const secondPart = pair.substring(i);

        if (currencies.includes(firstPart) || currencies.includes(secondPart)) {
            return `${firstPart}/${secondPart}`;
        }
    }

    return pair; // Return original if no match found
}

export function getCurrency(name: string, index: number) {
    const pair = addSlashToFxPair(name);
    const currency = pair.split('/')[index];
    if (currency) return currency.toLowerCase();
}
export function getFullWeeksStartAndEndInMonth(month: number, year: number) {
    let weeks = [],
        firstDate = new Date(year, month, 1),
        lastDate = new Date(year, month + 1, 0),
        numDays = lastDate.getDate();

    let start = 1;
    let end;
    if (firstDate.getDay() === 1) {
        end = 7;
    } else if (firstDate.getDay() === 0) {
        let preMonthEndDay = new Date(year, month, 0);
        start = preMonthEndDay.getDate() - 6 + 1;
        end = 1;
    } else {
        let preMonthEndDay = new Date(year, month, 0);
        start = preMonthEndDay.getDate() + 1 - firstDate.getDay() + 1;
        end = 7 - firstDate.getDay() + 1;
        weeks.push({
            start: start,
            end: end
        });
        start = end + 1;
        end = end + 7;
    }
    while (start <= numDays) {
        weeks.push({
            start: start,
            end: end
        });
        start = end + 1;
        end = end + 7;
        end = start === 1 && end === 8 ? 1 : end;
        if (end > numDays && start <= numDays) {
            end = end - numDays;
            weeks.push({
                start: start,
                end: end
            });
            break;
        }
    }
    // *** the magic starts here
    return weeks.map(({ start, end }, index) => {
        const sub = +(start > end && index === 0);
        return Array.from({ length: 7 }, (_, index) => {
            const date = new Date(year, month - sub, start + index);
            return {
                date: date.getDate(),
                month: date.getMonth(),
                day: date.toLocaleString('en', { weekday: 'long' })
            };
        });
    });
}
export function getMonthsBetweenDates(
    startDate: string | Date,
    endDate: string | Date,
    withId: boolean
) {
    let start = new Date(startDate);
    let end = new Date(endDate);

    if (start > end) {
        [start, end] = [end, start];
    }

    let months = [];
    let current = new Date(start.getFullYear(), start.getMonth(), 1);
    let id = 0;

    while (current <= end) {
        let monthYear = current.toLocaleString('en', {
            month: 'long',
            year: 'numeric'
        });
        withId
            ? months.push({
                  month: current.getMonth(),
                  year: current.getFullYear(),
                  name: monthYear,
                  id
              })
            : months.push(monthYear);
        id = id + 1;
        current.setMonth(current.getMonth() + 1);
    }

    return months;
}

export function getYearsBetweenDates(
    startDate: string | Date,
    endDate: string | Date,
    withId: boolean
) {
    let start = new Date(startDate);
    let end = new Date(endDate);

    if (start > end) {
        [start, end] = [end, start];
    }

    let years = [];
    let startYear = start.getFullYear();
    let endYear = end.getFullYear();
    let id = 0;

    for (let year = startYear; year <= endYear; year++) {
        withId
            ? years.push({ value: year, name: year.toString(), id })
            : years.push(year.toString());
        id = id + 1;
    }

    return years;
}
export function subtractDays(isoString: string, days: number) {
    let date = new Date(isoString);
    let timestamp = date.getTime();

    let newTimestamp = timestamp - days * 24 * 60 * 60 * 1000;

    let newDate = new Date(newTimestamp).toISOString().split('.')[0] + 'Z';

    return newDate;
}

export function getYearStructure(year: number): Month[] {
    function getMonthWeeks(month: number, year: number): Month {
        let weeks: { start: number; end: number }[] = [],
            firstDate = new Date(year, month, 1),
            lastDate = new Date(year, month + 1, 0),
            numDays = lastDate.getDate();

        let start = 1;
        let end;
        if (firstDate.getDay() === 1) {
            end = 7;
        } else if (firstDate.getDay() === 0) {
            let preMonthEndDay = new Date(year, month, 0);
            start = preMonthEndDay.getDate() - 6 + 1;
            end = 1;
        } else {
            let preMonthEndDay = new Date(year, month, 0);
            start = preMonthEndDay.getDate() + 1 - firstDate.getDay() + 1;
            end = 7 - firstDate.getDay() + 1;
            weeks.push({
                start: start,
                end: end
            });
            start = end + 1;
            end = end + 7;
        }
        while (start <= numDays) {
            weeks.push({
                start: start,
                end: end
            });
            start = end + 1;
            end = end + 7;
            end = start === 1 && end === 8 ? 1 : end;
            if (end > numDays && start <= numDays) {
                end = end - numDays;
                weeks.push({
                    start: start,
                    end: end
                });
                break;
            }
        }

        return weeks.map(({ start, end }, index): Week => {
            const sub = +(start > end && index === 0);
            return Array.from({ length: 7 }, (_, index): Day => {
                const date = new Date(year, month - sub, start + index);
                return {
                    date: date.getMonth() === month ? date.getDate() : 0,
                    day: date.toLocaleString('en', { weekday: 'long' })
                };
            });
        });
    }

    let yearStructure = [];
    for (let month = 0; month < 12; month++) {
        yearStructure.push(getMonthWeeks(month, year));
    }

    return yearStructure;
}
export function findEarliestDate(dateStrings: string[]) {
    const dates = dateStrings.map((dateStr) => new Date(dateStr));
    const earliestDate = new Date(
        Math.min(...dates.map((date) => date.getTime()))
    );

    return earliestDate;
}
